/**
 * T3-3.2 / T3-4.1：详情页单张裁剪信息（检测详情仅打日志，页面不展示）。
 */

import { resolveCropResultTiles } from './imageCropDetailPrints';

/** @returns {string | null} 如 "92%" */
export function formatCropConfidence(confidence) {
  if (confidence == null) return null;
  const value = Number(confidence);
  if (!Number.isFinite(value)) return null;
  const percent = Math.round(value * 100);
  return `${Math.max(0, Math.min(100, percent))}%`;
}

export function shouldShowCropUsedFallback(usedFallback) {
  return Boolean(usedFallback);
}

function resolveDetectLabel(row) {
  const display = row?.cropPositionDisplay;
  const label = display?.label ?? row?.cropPosition?.label;
  return label ? String(label).trim() : '';
}

function resolveDetectConfidenceText(row) {
  const display = row?.cropPositionDisplay;
  return display?.confidenceText
    ?? formatCropConfidence(display?.confidence ?? row?.cropPosition?.confidence);
}

/**
 * 检测详情日志 payload；无 Qwen 检测信息时返回 null。
 * @param {{ imageId?: string, pipeline?: string, cropPosition?: object | null, cropPositionDisplay?: object | null, usedFallback?: boolean }} row
 */
export function buildImageCropDetectLogPayload(row) {
  const display = row?.cropPositionDisplay;
  const pipelineLabel = display?.pipelineLabel;
  const isQwenBbox = Boolean(display?.isQwenBbox ?? row?.isQwenBbox);
  if (!isQwenBbox && !pipelineLabel) {
    return null;
  }

  const label = resolveDetectLabel(row);
  const confidenceText = resolveDetectConfidenceText(row);
  if (!pipelineLabel && !label && !confidenceText) {
    return null;
  }

  return {
    imageId: row?.imageId != null ? String(row.imageId) : undefined,
    pipeline: pipelineLabel || undefined,
    label: label || undefined,
    confidence: confidenceText || undefined,
    usedFallback: Boolean(display?.usedFallback ?? row?.usedFallback),
  };
}

/** 同一张图、同一组检测字段只打一次，避免轮询重复刷屏。 */
export function buildImageCropDetectLogKey(row) {
  const payload = buildImageCropDetectLogPayload(row);
  if (!payload) return '';
  return [
    payload.imageId || '',
    payload.pipeline || '',
    payload.label || '',
    payload.confidence || '',
    payload.usedFallback ? '1' : '0',
  ].join('|');
}

/** 浏览器控制台日志（检测 / 印花 / 置信度不在页面展示）。 */
export function logImageCropDetectInfo(row) {
  const payload = buildImageCropDetectLogPayload(row);
  if (!payload) return;
  console.info('[image-crop] detect result', payload);
}

/**
 * 页面仅保留 fallback / 多印花提示；检测、印花、置信度见 {@link logImageCropDetectInfo}。
 * @param {{ cropPosition?: object | null, cropPositionDisplay?: object | null, usedFallback?: boolean }} row
 * @returns {Array<{ key: string, label: string, value: string }>}
 */
export function buildImageCropDetailInfoItems(row) {
  const display = row?.cropPositionDisplay;
  const items = [];

  const printCount = resolveCropResultTiles(row).length;
  if (printCount > 1) {
    items.push({
      key: 'multi-print',
      label: '印花',
      value: `检测到 ${printCount} 处`,
    });
  }

  if (shouldShowCropUsedFallback(display?.usedFallback ?? row?.usedFallback)) {
    items.push({ key: 'fallback', label: '算法', value: '本地 fallback' });
  }

  return items;
}
